/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: public surface. See MODULE-MAP.md for source-line provenance.

import { createDefaultReport } from "./default-report"
import { adaptReviewExport } from "./adapter"
import { applyHandoffPackage, normalizeCandidateReportStructure } from "./import"
import { currentHeartRateBpm, ensureFeatureBaselines, sourceIdForFeed } from "./features"
import { setAutoPatientCopy } from "./copy"
import { effectiveFeatureColorClass } from "./status"
import { clearFeatureApprovals, clearReportApproval } from "./review"
import {
  INCLUSION_ACCEPTED,
  INCLUSION_EXCLUDED,
  INCLUSION_UNRESOLVED,
  MEASUREMENT_UNAVAILABLE_ORIGIN,
  reconcileReportInclusionStates,
} from "./inclusion"
import { syncStructuralSelection } from "./structural-sources"
import {
  reapplyTimingSelection,
  recalculateTimingFeatureFromSources,
  timingSourceRecordings,
  timingValuesDerivedFromSources,
} from "./timing-sources"

export * from "./constants"
export * from "./parse"
export * from "./zones"
export * from "./features"
export * from "./timing-pattern"
export * from "./status"
export * from "./marker"
export * from "./display"
export * from "./copy"
export * from "./governance"
export * from "./review"
export * from "./inclusion"
export * from "./default-report"
export * from "./import"
export * from "./adapter"
export * from "./exam-rollup"
export * from "./third-party-sources"
export * from "./timing-sources"
export * from "./structural-sources"
export * from "./structural-sources"

// Convenience: full pipeline from OUR export payload to a ready report model
// (mirrors initializeReport() minus DOM/localStorage: import -> normalize -> baselines).
export function createReportFromReviewExport(exportPayload, options = {}) {
  const adapted = adaptReviewExport(exportPayload, options)
  if (!adapted.ok) return { ...adapted, report: null, importStatus: null }
  const report = createDefaultReport(options)
  const imported = applyHandoffPackage(report, adapted.pkg, options)
  normalizeCandidateReportStructure(report)
  reconcileReportInclusionStates(report.features)
  ensureFeatureBaselines(report.features)
  return { ...adapted, report, importStatus: imported.importStatus, applied: imported.applied }
}

// Fields a clinician edits on a report card (typed value / marker drag, the
// Clinician Note, feed decisions, and whether the card is in the report). A
// rebase carries these across an updated import so the clinician's work is
// not lost. Patient-facing copy is product-controlled and always regenerated.
const EDITED_VALUE_FIELDS = [
  "observed",
  "status",
  "aiStatus",
  "markerPosition",
  "clinicianAssessmentMode",
  "clinicianRangeBpm",
  "sourceRangeBpm",
  "mixedRange",
  // The value range a SYS:DIA card derives from its included recordings,
  // and the medians its marker sits at
  "clinicianRatioRange",
  "clinicianSysDiaDurationRange",
  "representativeSdRatio",
  "representativeSysMs",
  "representativeDiaMs",
  "timingPatternLabel",
  // The Structural Sound recording the clinician chose as the card's evidence
  "sourceRecordingId",
  "sourcePosition",
]
const EDITED_NOTE_FIELDS = ["noteToDoctor", "noteToDoctorEdited"]
const INCLUSION_FIELDS = [
  "reportInclusionStatus",
  "reportInclusionReviewedAt",
  "reportInclusionPreviousStatus",
  "reportInclusionOrigin",
  "manualAssessmentStarted",
  "savedReportBaseline",
]

// Which recordings the clinician left out of a timing card, and whether the
// card's value is still the one those recordings give (nothing typed over it)
const snapshotTimingSelection = (features, feature) => ({
  excluded: new Map(
    timingSourceRecordings(feature)
      .filter((source) => source.included === false)
      .map((source) => [String(source.recordingId), source.selectionChangedAt || null])
  ),
  derived: timingValuesDerivedFromSources(features, feature),
})

const snapshotFeatureEdits = (features, feature) => ({
  reviewTouched: !!feature.reviewTouched,
  timingSelection: snapshotTimingSelection(features, feature),
  inclusion: Object.fromEntries(INCLUSION_FIELDS.map((key) => [key, feature[key]])),
  noteToDoctorStale: !!feature.noteToDoctorStale,
  importedBaseline: feature.importedBaseline || null,
  values: Object.fromEntries(EDITED_VALUE_FIELDS.map((key) => [key, feature[key]])),
  notes: Object.fromEntries(EDITED_NOTE_FIELDS.map((key) => [key, feature[key]])),
  sourceReviews: new Map(
    (feature.sources || [])
      .filter((source) => (source.reviewStatus && source.reviewStatus !== "unreviewed") || source.evidenceDisposition)
      .map((source) => [
        sourceIdForFeed(source),
        {
          reviewStatus: source.reviewStatus,
          evidenceDisposition: source.evidenceDisposition || null,
          dispositionAt: source.dispositionAt || null,
          rejectReasonCode: source.rejectReasonCode || null,
          rejectReasonText: source.rejectReasonText || null,
        },
      ])
  ),
})

const baselineResultChanged = (before, after) =>
  !before || !after || before.observed !== after.observed || before.status !== after.status

// Re-derive an EXISTING report document from a newer review export, keeping
// the clinician's edits. applyHandoffPackage overwrites every imported card
// outright (value, status, marker, sources; edited notes survive but are
// flagged stale unconditionally), so the clinician's work is captured first
// and restored afterwards:
// - a card whose value the clinician changed keeps that value, marker and
//   notes; its notes are flagged stale when the imported result differs
//   from the result the edit was made against (the stored baseline)
// - a card with only edited notes keeps them; they are flagged stale only
//   when the imported result actually changed
// - feed accept/reject decisions are kept
// - a card the clinician excluded stays excluded (taking the new evidence's
//   value unless the clinician had set one), and a card the clinician
//   assessed by hand and saved stays in the report with that assessment;
//   every other card takes the state of the new evidence (newly accepted
//   evidence arrives included, a domain no longer accepted goes back to
//   needing a decision, candidate evidence arrives pending)
// - a card the import left out for want of a measurement is not a clinician
//   exclusion: it follows the new evidence, in or out. Where the clinician
//   had assessed such a card by hand, in a document from before these cards
//   required a measurement, that assessment is kept
// - a timing card's recordings are rebuilt from the new snapshot; where the
//   clinician's work is kept, the recordings left out before are left out
//   again if the review still accepts them, and the card's value follows
//   the recordings it now includes unless the clinician had typed over it
// - patient-facing copy is regenerated for whatever result each card keeps
// - every approval is cleared: a rebased report is back in review
// The recording chips are rebuilt from the new snapshot (the import would
// otherwise keep chips for recordings no longer in the snapshot), and each
// card's baseline now points at the new import, so "rewind" restores it.
export function rebaseReportOntoReviewExport(report, exportPayload, options = {}) {
  const adapted = adaptReviewExport(exportPayload, options)
  if (!adapted.ok) return { ...adapted, report, applied: 0, importStatus: null }
  const features = report.features || []
  const edits = new Map(features.map((feature) => [feature.name, snapshotFeatureEdits(features, feature)]))
  report.recordings = []
  // The decision record of an earlier confirm step, no longer written
  delete report.timingDiscrepancyResolution
  const imported = applyHandoffPackage(report, adapted.pkg, options)
  ;(report.features || []).forEach((feature, index) => {
    const before = edits.get(feature.name)
    if (!before) return
    const changed = baselineResultChanged(before.importedBaseline, feature.importedBaseline)
    const beforeState = before.inclusion.reportInclusionStatus
    const nowIncluded = feature.reportInclusionStatus === INCLUSION_ACCEPTED
    const assessedByHand = !!before.inclusion.manualAssessmentStarted
    const unavailableNow = !!feature.measurementUnavailable
    // The clinician's own exclusion, of a card the new evidence can state
    const excluded =
      beforeState === INCLUSION_EXCLUDED &&
      before.inclusion.reportInclusionOrigin !== MEASUREMENT_UNAVAILABLE_ORIGIN &&
      !(unavailableNow && !assessedByHand)
    // An assessment the clinician made by hand and saved to the report
    const savedByHand = beforeState === INCLUSION_ACCEPTED && assessedByHand
    // An unsaved manual assessment on a card that still has nothing behind it
    const openDraft = !nowIncluded && assessedByHand && !excluded && !savedByHand
    // Documents saved before cards carried a state: an edited card was in
    // the report, and keeps its edit while it still is
    const editedBeforeStates = !beforeState && before.reviewTouched && nowIncluded
    // The clinician's value outlives the new evidence only where the
    // clinician set one; an exclusion alone carries no value
    const keepsClinicianWork = savedByHand || openDraft || editedBeforeStates || (excluded && assessedByHand)
    // The card's place in the report first: the patient-facing copy below
    // is worded by it
    if (keepsClinicianWork && unavailableNow) {
      // The clinician's assessment stands in for the missing measurement
      feature.measurementUnavailable = false
      feature.measurementUnavailableReason = ""
      feature.reportInclusionStatus = INCLUSION_UNRESOLVED
      feature.reportInclusionOrigin = null
    }
    if (excluded || savedByHand) Object.assign(feature, before.inclusion)
    else if (openDraft) feature.manualAssessmentStarted = true
    else if (
      nowIncluded &&
      before.inclusion.reportInclusionOrigin &&
      before.inclusion.reportInclusionOrigin !== MEASUREMENT_UNAVAILABLE_ORIGIN
    ) {
      feature.reportInclusionOrigin = before.inclusion.reportInclusionOrigin
    }
    if (keepsClinicianWork) {
      // The recordings the clinician left out stay out while the review
      // still offers them; the card's value follows its recordings unless
      // the clinician had typed over it
      if (reapplyTimingSelection(feature, before.timingSelection.excluded)) {
        recalculateTimingFeatureFromSources(report.features, index, false)
      }
      if (!before.timingSelection.derived) Object.assign(feature, before.values)
      // A Structural Sound recording the clinician chose stays chosen while
      // the new evidence still lists it
      syncStructuralSelection(report.features, feature)
      Object.assign(feature, before.notes)
      feature.reviewTouched = before.reviewTouched
      // Patient-facing copy follows the result the card keeps
      setAutoPatientCopy(feature, effectiveFeatureColorClass(feature, currentHeartRateBpm(report.features)))
    } else if (before.notes.noteToDoctorEdited) {
      Object.assign(feature, before.notes)
      feature.reviewTouched = true
    }
    // A Clinician Note written against an earlier result needs the
    // clinician's decision once the imported result differs
    feature.noteToDoctorStale = before.noteToDoctorStale || (changed && !!before.notes.noteToDoctorEdited)
    feature.noteToPatientStale = false
    ;(feature.sources || []).forEach((source) => {
      const review = before.sourceReviews.get(sourceIdForFeed(source))
      if (review) Object.assign(source, review)
    })
  })
  normalizeCandidateReportStructure(report)
  ensureFeatureBaselines(report.features)
  clearFeatureApprovals(report.features)
  clearReportApproval(report)
  return { ...adapted, ok: true, report, importStatus: imported.importStatus, applied: imported.applied }
}
